// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { BankUploadSummaryRow } from '@client/banks/components/shared/bank-upload-summary-list';
import summaryListStyles from '@client/banks/components/shared/bank-upload-summary-list/bank-upload-summary-list.module.css';
import type { BankDebt } from '@client/banks/types/debt';
import { Typography } from '@mui/material';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import type { FC } from 'react';
import { DebtsListHeader } from './debts-list-header';
import styles from './debts-list.module.css';

export type DebtsListHeaderProps = {
  onAdd: () => void;
  onImportExcel?: () => void;
};

export type DebtSummaryItem = {
  index: number;
  debt: BankDebt;
  meta: string;
  pendingAmountLabel: string;
};

export type DebtsListProps = {
  items: DebtSummaryItem[];
  isEmpty: boolean;
  onAdd: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onImportExcel?: () => void;
};

export const DebtsList: FC<DebtsListProps> = ({
  items,
  isEmpty,
  onAdd,
  onEdit,
  onDelete,
  onImportExcel,
}) => (
  <div
    className="flex min-h-0 flex-1 flex-col"
    data-testid="bank-upload-debts-list"
  >
    <DebtsListHeader onAdd={onAdd} onImportExcel={onImportExcel} />

    <section className={styles['debts-summary']} aria-live="polite">
      <ul className={summaryListStyles['summary-list']}>
        {isEmpty ? (
          <li
            className={summaryListStyles['summary-list-empty']}
            data-testid="bank-upload-debts-list-empty"
          >
            <Typography
              variant="body2"
              className={summaryListStyles['summary-list-empty-message']}
            >
              <Translation
                i18nKey="aggregator.create-bank.data-section.empty-list-hint"
                ns="aggregator"
              />
            </Typography>
          </li>
        ) : (
          items.map(item => (
            <BankUploadSummaryRow
              key={`${item.debt.iban}-${item.debt.name}-${item.index}`}
              testIdPrefix="bank-upload-debt"
              index={item.index}
              title={item.debt.name}
              meta={item.meta}
              valueLabel={item.pendingAmountLabel}
              onOpen={onEdit}
              onDelete={onDelete}
            />
          ))
        )}
      </ul>
    </section>
  </div>
);

DebtsList.displayName = 'DebtsList';
